import type {
  CvHealthFinding,
  CvHealthLatestEnvelope,
  CvHealthProofType,
  CvHealthReport,
  CvHealthTargetKey,
} from "@tabbio/types";
import { normalizeCvLinkValue, isCvLinkKey, type CvLinkKey } from "@tabbio/utils/cv-profile-links";
import { countSuggestions } from "./cv-health-model";

/**
 * CV Health in the CV editor: the pure rules behind the dock control and the
 * editor sheet. No React and no platform code, so the `.tsx` / `.web.tsx`
 * twins, the hooks and the tests share one implementation.
 *
 *   - which CV targets can be checked and how they are cached
 *   - what the dock control says
 *   - the automatic recheck of a changed CV (once per revision)
 *   - the previous score of a CV, for "Up from 61"
 *   - the professional-link proposal
 *
 * Changes are never written from the client: the server applies proposals
 * and undoes them (`applyCvHealthChanges` / `undoCvHealthChanges` in
 * `cv-health-api.ts`), then rechecks.
 */

// ─── Targets and cache ──────────────────────────────────────────────────

/**
 * Delegated targets (a CV someone shared with this user) are not checked:
 * the report and its proposals belong to the owner. The editor falls back to
 * the setup ring there.
 */
export function isCvHealthTargetSupported(target?: string | null): boolean {
  const value = target?.trim() ?? "";
  return !value.startsWith("grant:") && !value.startsWith("access:");
}

/** The report target for an editor target: no target is the Main CV. */
export function toCvHealthTargetKey(target?: string | null): CvHealthTargetKey {
  return target?.trim() || "master";
}

/**
 * Lives under `['cv', target]`, so `invalidateCvDocumentQueries` refreshes the
 * latest report after every CV edit (that is what turns it stale).
 */
export function cvHealthQueryKey(target?: string | null) {
  return ["cv", target?.trim() || "self", "health"] as const;
}

/** Master writes change the Main CV; every other target is one CV row. */
export function toCvHealthTargetKind(target?: string | null): "master" | "cv" {
  return toCvHealthTargetKey(target) === "master" ? "master" : "cv";
}

// ─── Dock control ───────────────────────────────────────────────────────

export type CvHealthDockState = "idle" | "checking" | "suggestions" | "good" | "stale";

export interface CvHealthDockModel {
  state: CvHealthDockState;
  count: number;
  /** `seeker` key for the status line; pass `count` when `state` is suggestions. */
  statusKey: string;
}

const DOCK_STATUS_KEYS: Record<CvHealthDockState, string> = {
  idle: "cv.health.checkThisCv",
  checking: "cv.health.checking",
  suggestions: "cv.health.suggestions",
  good: "cv.health.looksGood",
  stale: "cv.health.checkAgain",
};

export interface CvHealthDockOptions {
  /** The automatic review of the current revision could not start. */
  autoRecheckFailed?: boolean;
  /**
   * The editor sheet gave up following the running report (its polls kept
   * failing): it says "We couldn't check your CV again", so the dock asks
   * for a new check instead of "Checking…" for good.
   */
  runAbandoned?: boolean;
}

/**
 * What the dock control says. Order matters: a running review wins; a
 * changed CV reads "Checking…" because it is rechecked on its own (see
 * `createCvHealthAutoRecheckStore`), and only asks "Check again" when that
 * automatic review failed. A failed or unreadable report never claims
 * "Looks good": it asks for a new check.
 */
export function deriveCvHealthDockModel(
  latest: Pick<CvHealthLatestEnvelope, "report" | "isStale"> | null | undefined,
  options: CvHealthDockOptions = {},
): CvHealthDockModel {
  const report = latest?.report ?? null;
  const build = (state: CvHealthDockState, count = 0): CvHealthDockModel => ({
    state,
    count,
    statusKey: DOCK_STATUS_KEYS[state],
  });
  if (!report) return build("idle");
  if (report.status === "running") return build(options.runAbandoned ? "stale" : "checking");
  if (latest?.isStale) return build(options.autoRecheckFailed ? "stale" : "checking");
  if (report.status === "failed" || report.status === "unreadable") return build("stale");
  const count = countSuggestions(report);
  if (count > 0) return build("suggestions", count);
  return build("good");
}

// ─── Automatic recheck of a changed CV ──────────────────────────────────

export type CvHealthAutoRecheckStatus = "starting" | "started" | "failed";

export interface CvHealthAutoRecheckEntry {
  /** The revision the automatic review was started for. */
  hash: string;
  status: CvHealthAutoRecheckStatus;
}

/**
 * Remembers, per CV target, the one automatic `editor_recheck` of the
 * current revision. `claim` succeeds once per new revision hash, so every
 * mounted copy of the hook (and every remount) agrees that a revision was
 * already rechecked; `settle` records whether the start succeeded, which is
 * the only case where the dock falls back to "Check again". Subscribable for
 * `useSyncExternalStore`; each entry object is replaced, never mutated.
 */
export function createCvHealthAutoRecheckStore() {
  const entries = new Map<string, CvHealthAutoRecheckEntry>();
  const listeners = new Set<() => void>();
  const emit = () => {
    for (const listener of listeners) listener();
  };
  return {
    get(target: string): CvHealthAutoRecheckEntry | null {
      return entries.get(target) ?? null;
    },
    /** True exactly once per `target` + `hash`: the caller starts the review. */
    claim(target: string, hash: string): boolean {
      if (!hash || entries.get(target)?.hash === hash) return false;
      entries.set(target, { hash, status: "starting" });
      emit();
      return true;
    },
    settle(target: string, hash: string, status: Exclude<CvHealthAutoRecheckStatus, "starting">) {
      const current = entries.get(target);
      if (!current || current.hash !== hash || current.status === status) return;
      entries.set(target, { hash, status });
      emit();
    },
    subscribe(listener: () => void): () => void {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

export type CvHealthAutoRecheckStore = ReturnType<typeof createCvHealthAutoRecheckStore>;

/**
 * The running reports the editor sheet stopped following, per CV target (its
 * polls kept failing). The cached running report stays, so without this the
 * dock would say "Checking…" while the sheet says the check failed. Keyed by
 * report id: any newer report is no longer abandoned. Subscribable for
 * `useSyncExternalStore`.
 */
export function createCvHealthAbandonedRunStore() {
  const entries = new Map<string, string>();
  const listeners = new Set<() => void>();
  return {
    get(target: string): string | null {
      return entries.get(target) ?? null;
    },
    mark(target: string, reportId: string) {
      if (entries.get(target) === reportId) return;
      entries.set(target, reportId);
      for (const listener of listeners) listener();
    },
    subscribe(listener: () => void): () => void {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

// ─── Previous score ─────────────────────────────────────────────────────

/**
 * The score a CV had before its current report, for "Up from 61". The latest
 * endpoint only returns the newest report, so the app remembers, per target,
 * the last settled report it saw; a new settled report makes that one the
 * previous. `observe` is idempotent per report id (safe to call on every
 * render) and only counts reports with a score (complete or partial).
 */
export function createCvHealthScoreHistory() {
  const last = new Map<string, { reportId: string; score: number | null }>();
  const previous = new Map<string, { reportId: string; score: number | null }>();
  return {
    observe(target: string, report: CvHealthReport | null | undefined): number | null {
      if (!report || (report.status !== "complete" && report.status !== "partial")) {
        return null;
      }
      const seen = last.get(target);
      if (!seen || seen.reportId !== report.id) {
        if (seen) previous.set(target, { reportId: report.id, score: seen.score });
        last.set(target, { reportId: report.id, score: report.score });
      }
      const before = previous.get(target);
      return before && before.reportId === report.id ? before.score : null;
    },
  };
}

// ─── Professional link ──────────────────────────────────────────────────

const PROOF_LINK_KEYS: Record<CvHealthProofType, CvLinkKey> = {
  github: "github",
  portfolio: "website",
  linkedin: "linkedin",
};

export function proofTypeToLinkKey(proofType?: CvHealthProofType): CvLinkKey {
  return PROOF_LINK_KEYS[proofType ?? "linkedin"];
}

/**
 * "Add a professional link" reuses the proposal review with an empty,
 * editable link field: the same finding, with a link proposal from nothing.
 */
export function buildProofLinkFinding(finding: CvHealthFinding): CvHealthFinding {
  return {
    ...finding,
    proposal: {
      target: { kind: "link", key: proofTypeToLinkKey(finding.proofType) },
      before: "",
      after: "",
      source: "deterministic",
    },
  };
}

/** A link change saves the canonical URL, or nothing when it does not validate. */
export function normalizeCvHealthLink(key: string, value: string): string | null {
  if (!isCvLinkKey(key)) return null;
  return normalizeCvLinkValue(key, value);
}
